import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Copy, RefreshCw, Save } from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import type { ReleaseDetail, ReleaseNotes } from "../../shared/types/api";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  InlineAlert,
  LoadingState,
  Textarea,
} from "../../shared/ui";
import { useReleaseMutation } from "./useReleaseMutation";

/**
 * Release notes, generated and then edited (§64).
 *
 * <p>The draft comes from the scope because that is the authoritative list of what shipped —
 * notes typed from memory at the end of a long week reliably omit the one ticket a customer asks
 * about. But it is a draft: ticket titles are written by and for the team, and "Fix NPE in
 * OrderMapper" is not a sentence to show a customer, so nothing is published until somebody has
 * read it through.
 *
 * <p>Regenerating replaces the editor's contents, so it warns first when there are unsaved edits.
 * Losing ten minutes of rewriting to a mis-click is a small disaster and an avoidable one.
 */
export function ReleaseNotesCard({
  release,
  onChanged,
}: {
  release: ReleaseDetail;
  onChanged: () => void;
}) {
  const { can } = useAuth();
  const [draft, setDraft] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const { run, error, saving } = useReleaseMutation(release.id);

  const notes = useQuery({
    queryKey: ["release-notes", release.id],
    queryFn: () => api.get<ReleaseNotes>(`/api/v1/releases/${release.id}/release-notes`),
  });

  useEffect(() => {
    if (notes.data && !dirty) {
      setDraft(notes.data.markdown);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes.data]);

  const regenerate = () => {
    if (dirty && !window.confirm("Regenerating replaces your edits. Continue?")) {
      return;
    }
    setDirty(false);
    void notes.refetch();
  };

  const save = () =>
    void run(
      () => api.post(`/api/v1/releases/${release.id}/release-notes`, { markdown: draft }),
      () => {
        setDirty(false);
        setSaved(true);
        onChanged();
      },
    );

  return (
    <Card>
      <CardHeader
        title="Release notes"
        subtitle="Assembled from the scope. Edit before anybody outside the team reads it."
        action={
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              icon={<Copy className="h-4 w-4" />}
              onClick={() => void navigator.clipboard?.writeText(draft)}
            >
              Copy
            </Button>
            <Button
              size="sm"
              variant="secondary"
              icon={<RefreshCw className="h-4 w-4" />}
              onClick={regenerate}
            >
              Regenerate
            </Button>
            {can("RELEASE_DOCUMENT_UPLOAD") && (
              <Button
                size="sm"
                icon={<Save className="h-4 w-4" />}
                loading={saving}
                onClick={save}
              >
                Save as document
              </Button>
            )}
          </div>
        }
      />
      <CardBody className="space-y-3">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}
        {saved && !dirty && (
          <InlineAlert tone="success">
            Saved as this release's notes document. Regenerating and saving again replaces it.
          </InlineAlert>
        )}

        {notes.isPending ? (
          <LoadingState label="Assembling the notes…" />
        ) : (
          <>
            <Textarea
              rows={18}
              value={draft}
              className="font-mono text-xs"
              onChange={(event) => {
                setDraft(event.target.value);
                setDirty(true);
                setSaved(false);
              }}
            />
            <p className="text-xs text-ink-500">
              Markdown. Known issues are included because a limitation nobody was told about is
              the one that generates the support call.
            </p>
          </>
        )}
      </CardBody>
    </Card>
  );
}
